import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Checkbox, Collapse, Stack, Typography } from '@mui/material';
import { IDomain } from '@store/services/projects/types';
import { themePalette } from '@theme/muiTheme';
import { findWideDomain } from '@utils/domains';
import { DropdownIcon } from '@utils/iconsDefs';
import {
  buildTree,
  collectSubtreeIds,
  ITreeItem,
  syncWideDomainSelection,
  toggleScopeSelection,
} from '@views/Project/helpers';

const collectParentIds = (nodes: ITreeItem[]): string[] =>
  nodes.flatMap((node) =>
    node.children?.length ? [node.id, ...collectParentIds(node.children)] : [],
  );

type RowProps = {
  node: ITreeItem;
  depth: number;
  selectedSet: Set<string>;
  expanded: Set<string>;
  onToggleSelect: (node: ITreeItem) => void;
  onToggleExpand: (id: string) => void;
  isWide?: boolean;
};

const TreeRow: FC<RowProps> = ({
  node,
  depth,
  selectedSet,
  expanded,
  onToggleSelect,
  onToggleExpand,
  isWide = false,
}) => {
  const hasChildren = !!node.children?.length;
  const isChecked = selectedSet.has(node.id);
  const isOpen = expanded.has(node.id);

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          cursor: hasChildren ? 'pointer' : 'default',
          '&:hover': { backgroundColor: themePalette.grey[400] },
          pl: `${depth * 24}px`,
        }}
        pr="24px"
        py="16px"
        onClick={() => hasChildren && onToggleExpand(node.id)}
      >
        <Stack direction="row" alignItems="center" gap={2}>
          <Checkbox
            checked={isChecked}
            onClick={(event) => {
              event.stopPropagation();
              onToggleSelect(node);
            }}
          />
          <Typography variant={isWide ? 'h5' : 'body1'}>{node.name}</Typography>
        </Stack>

        {hasChildren && (
          <DropdownIcon
            fill={themePalette.grey[1000]}
            style={{ transform: isOpen ? 'rotate(180deg)' : '', minWidth: 20 }}
          />
        )}
      </Stack>

      {hasChildren && (
        <Collapse in={isOpen} unmountOnExit>
          {node.children?.map((child) => (
            <TreeRow
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedSet={selectedSet}
              expanded={expanded}
              onToggleSelect={onToggleSelect}
              onToggleExpand={onToggleExpand}
            />
          ))}
        </Collapse>
      )}
    </>
  );
};

type Props = {
  domains: IDomain[];
  selected: string[];
  onChange: (ids: string[]) => void;
};

export const AddScopeTree: FC<Props> = ({ domains, selected, onChange }) => {
  const { wideLeaf, wideNode, childRows } = useMemo(() => {
    const roots = buildTree(domains);
    const wideId = findWideDomain(domains)?.id;
    const wideRoot = roots.find((root) => root.id === wideId);

    if (!wideRoot) {
      return {
        wideLeaf: null as ITreeItem | null,
        wideNode: null as ITreeItem | null,
        childRows: roots,
      };
    }

    return {
      wideLeaf: { id: wideRoot.id, name: wideRoot.name, children: [] },
      wideNode: wideRoot,
      childRows: [
        ...roots.filter((root) => root.id !== wideId),
        ...(wideRoot.children ?? []),
      ],
    };
  }, [domains]);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const didInitExpand = useRef(false);
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  useEffect(() => {
    if (didInitExpand.current || childRows.length === 0) return;
    setExpanded(new Set(collectParentIds(childRows)));
    didInitExpand.current = true;
  }, [childRows]);

  const toggleExpand = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  const toggleSelect = (node: ITreeItem) => {
    const isWideRow = !!wideNode && wideNode.id === node.id;

    const next = toggleScopeSelection({
      selected,
      nodeId: node.id,
      isChecked: !selectedSet.has(node.id),
      subtreeIds: collectSubtreeIds(isWideRow ? wideNode : node),
      cascadesOnUncheck: isWideRow,
    });

    onChange(isWideRow ? next : syncWideDomainSelection(next, wideNode));
  };

  return (
    <Box>
      {wideLeaf && (
        <TreeRow
          node={wideLeaf}
          depth={0}
          selectedSet={selectedSet}
          expanded={expanded}
          onToggleSelect={toggleSelect}
          onToggleExpand={toggleExpand}
          isWide
        />
      )}

      {childRows.map((row) => (
        <TreeRow
          key={row.id}
          node={row}
          depth={wideLeaf ? 1 : 0}
          selectedSet={selectedSet}
          expanded={expanded}
          onToggleSelect={toggleSelect}
          onToggleExpand={toggleExpand}
        />
      ))}
    </Box>
  );
};
